import {
  NoteDto,
} from '@act/shared/data-transfer-objects';
import {
  PayloadAction,
  createAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { notesRequests } from '../requests/notes.request';
import { useAppSelector } from '../storeHooks';
import { NotesState } from '../types/noteTypes';
import { createApiThunk } from '../utils/apiThunk';
import { ActivitySelectorParams } from '../types/selectorTypes';
import { getGuideInteractions } from './docketSlice.slice';
import { feedSlice, getFeedItems } from './feedSlice.slice';
import { resetRedux } from '../actions/sharedActions';

// THUNKS/ACTIONS ==============================================================
export const createNotes = createApiThunk(notesRequests.createNotes);
export const updateNote = createApiThunk(notesRequests.updateNote);
export const deleteNote = createApiThunk(notesRequests.deleteNote);

export const createNoteSuccess = createAction<NoteDto>(
  'notes/createNoteSuccess'
);
export const updateNoteSuccess = createAction<NoteDto>(
  'notes/updateNoteSuccess'
);
export const deleteNoteSuccess = createAction<number>(
  'notes/deleteNoteSuccess'
);

// INITIAL STATE ===============================================================
export const notesAdapter = createEntityAdapter<NoteDto>();

const initialState: NotesState = {
  status: 'initial',
  notes: notesAdapter.getInitialState(),
};

// SLICE =======================================================================
export const notesSlice = createSlice({
  name: 'notes',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })

      .addCase(createNoteSuccess, (state, action: PayloadAction<NoteDto>) => {
        notesAdapter.addOne(state.notes, action.payload);
        state.status = 'success';
      })
      .addCase(updateNoteSuccess, (state, action: PayloadAction<NoteDto>) => {
        notesAdapter.upsertOne(state.notes, action.payload);
        state.status = 'success';
      })
      .addCase(deleteNoteSuccess, (state, action: PayloadAction<number>) => {
        notesAdapter.removeOne(state.notes, action.payload);
        state.status = 'success';
      })
      .addCase(getFeedItems.fulfilled, (state, action) => {
        notesAdapter.addMany(state.notes, action.payload.notes);
        state.status = 'success';
      })
      .addCase(feedSlice.actions.feedItemUpdated, (state, action) => {
        if (!action.payload.feedItemModelView.notes) return;

        notesAdapter.upsertMany(
          state.notes,
          action.payload.feedItemModelView.notes
        );
      })
      // Docket Interactions
      .addCase(getGuideInteractions.fulfilled, (state, action) => {
        notesAdapter.upsertMany(state.notes, action.payload.notes);
        state.status = 'success';
      });
  },
});

// REDUCER =====================================================================
export const notesReducer = notesSlice.reducer;

// SELECTORS ===================================================================
const selectNoteSliceState = state =>
  (state as { notes: typeof initialState })?.notes;
const selectNotesState = createSelector(selectNoteSliceState, s => s.notes);

export const { selectAll: selectNotes, selectEntities: selectNotesMap } =
  notesAdapter.getSelectors(selectNotesState);

export const selectNoteById = (id: number) =>
  createSelector(selectNotesMap, callOutcomes => {
    return callOutcomes[id];
  });

export const selectNotesByGuideAndTime: (
  state,
  paramObj: ActivitySelectorParams
) => NoteDto[] = createSelector(
  [
    selectNotes,
    (state, paramObj: ActivitySelectorParams) => paramObj.guideId,
    (state, paramObj: ActivitySelectorParams) => paramObj.startEpochTime,
    (state, paramObj: ActivitySelectorParams) => paramObj.endEpochTime,
  ],
  (notes, guideId, startEpochTime, endEpochTime) => {
    return notes.filter(
      n =>
        n.createdBy === guideId &&
        n.timestamp >= startEpochTime &&
        n.timestamp <= endEpochTime
    );
  }
);

// EXPORT HELPER ===============================================================
export const notesAppSelectors = {
  useSelectNoteById: id => useAppSelector(selectNoteById(id)),
};

// EFFECTS =====================================================================
// WEBSOCKET ACTIONS ===========================================================
